import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import format from "date-fns/format";
import { useTranslation } from "react-i18next";

import { acceptConversationItem } from "actions/acceptConversationItem";
import { completeConversationItem } from "actions/completeConversationItem";
import { rejectConversationItem } from "actions/rejectConversationItem";

import { STATUS_PERSON, DEAL_STATUS } from "constants/chatConstants";

import styles from "./styles.module.scss";

const ChatEntityItem = ({ item, onReply, conversationId }) => {
    const { t } = useTranslation();
    const profileId = useSelector((state) => state.profile.profileData.id);
    const lang = useSelector((state) => state.settings.lang);
    const dispatch = useDispatch();
    const isMyEntity = profileId === item.userId;

    const handleReply = () => {
        onReply(item);
    };

    const handleAccept = useCallback(() => {
        dispatch(acceptConversationItem(conversationId, item, isMyEntity, lang));
    }, [dispatch, conversationId, item, isMyEntity, lang]);
    const handleReject = useCallback(() => {
      dispatch(rejectConversationItem(conversationId, item, lang));
    }, [dispatch, conversationId, item, lang]);
    const handleComplete = useCallback(() => {
      dispatch(completeConversationItem(conversationId, item, isMyEntity, lang));
    }, [dispatch, conversationId, item, isMyEntity, lang]);

    const renderChoice = () => {
      const ownerStatus = item[STATUS_PERSON.OWNER_STATUS];
      const clientStatus = item[STATUS_PERSON.CLIENT_STATUS];

      const confirm = { visible: true, text: '', onClick: undefined };
      const cancel = { visible: true, text: '', onClick: undefined };

      if (ownerStatus === DEAL_STATUS.ACTIVE) {
        // owner must accept/decline first
        if (!isMyEntity) return null;

        confirm.text = t('dialog.ownerAcceptButton');
        confirm.onClick = handleAccept;

        cancel.text = t('dialog.ownerCancelButton');
        cancel.onClick = handleReject;
      } else if (ownerStatus === DEAL_STATUS.ACCEPTED && clientStatus === DEAL_STATUS.ACTIVE) {
        // hide accept button for owner and allow client to accept
        confirm.text = isMyEntity ? t('dialog.ownerAcceptButton') : t('dialog.clientAcceptButton');
        confirm.visible = !isMyEntity;
        confirm.onClick = isMyEntity ? undefined : handleAccept;

        cancel.text = isMyEntity ? t('dialog.ownerCancelButton') : t('dialog.clientCancelButton');
        cancel.onClick = handleReject;
      } else if (ownerStatus === DEAL_STATUS.ACCEPTED && clientStatus === DEAL_STATUS.ACCEPTED) {
        // owner must complete first
        if (!isMyEntity) return null;

        confirm.text = t('dialog.finishedButton');
        confirm.onClick = handleComplete;

        cancel.visible = false;
      } else if (ownerStatus === DEAL_STATUS.COMPLETED && clientStatus === DEAL_STATUS.ACCEPTED) {
        // hide complete button for owner and allow client to complete
        if (isMyEntity) return null;

        confirm.text = t('dialog.finishedButton');
        confirm.onClick = handleComplete;

        cancel.visible = false;
      } else {
        return null;
      }

      return (
        <>
          {confirm.visible && (
            <button onClick={confirm.onClick} className={`${styles.item__button} ${styles['item__button--approve']}`}>
              {confirm.text}
            </button>
          )}
          {cancel.visible && (
            <button onClick={cancel.onClick} className={`${styles.item__button} ${styles['item__button--decline']}`}>
              {cancel.text}
            </button>
          )}
        </>
      );
    };

    return (
      <>
        <div className={styles.item}>
          <div>
            <span className={styles.item__code}>
              {item.loading_address.country_code}
              {item.loading_address.post_code ? `, ${item.loading_address.post_code}` : ''}
            </span>
            <span className={styles.item__route}>
              {item.loading_address.city || item.loading_address.state_province || item.loading_address.country}
            </span>
            <span className={styles.item__date}>{format(new Date(item.loading_from), 'dd.MM')}</span>
          </div>
          <div>
            <span className={styles.item__code}>
              {item.unloading_address.country_code}
              {item.unloading_address.post_code ? `, ${item.unloading_address.post_code}` : ''}
            </span>
            <span className={styles.item__route}>
              {item.unloading_address.city || item.unloading_address.state_province || item.unloading_address.country}
            </span>
            <span className={styles.item__date}>{format(new Date(item.unloading_to), 'dd.MM')}</span>
          </div>
          {(item.designation_title || item.weight > 0) && (
            <div>
              <span className={styles.item__designation}>
                {item.designation_title}
                {item.designation_title && item.weight > 0 ? ', ' : ''}
                {item.weight > 0 ? `${item.weight}${t('units.tons')}` : ''}
              </span>
            </div>
          )}
          <div className={styles.item__buttons}>
            {renderChoice()}
            <button onClick={handleReply} className={`${styles.item__button} ${styles['item__button--reply']}`}>
              {t('dialog.replyButton')}
            </button>
          </div>
        </div>
      </>
    );
};

export default ChatEntityItem;
